"use client";

import { ChevronRightIcon } from "lucide-react";
import React from "react";

import { Check, Minus } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { getButtonVariantStylesAndSize, ButtonVariantTypes } from "components/ui/button";
import Contact from "components/Tiles/commonComponents/Contact";
import { APP_LANGUAGES } from "utils/language";

interface PricingPackage {
  name: string;
  id: string;
  href: string;
  iconUrl: string;
  featured: boolean;
  description: string;
  price?: string | { total: string; discounted: string };
  total?: string;
  includedFeatures: string[];
}

interface Feature {
  name: string;
  pricingPackages?: {
    [key: string]: boolean | string;
  };
}

interface Section {
  name: string;
  features?: Feature[];
}

interface PackageTableProps {
  title: string;
  contactLink?: string;
  pricingPackages?: PricingPackage[];
  sections?: Section[];
  language?: APP_LANGUAGES;
}

function classNames(...classes: (false | null | undefined | string)[]) {
  return classes.filter(Boolean).join(" ");
}

const formatToCurrency = (value: string) => {
  const parsed = Number(value);
  if (isNaN(parsed)) return value;

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  return formatter.format(parsed);
};

const PackageTable: React.FC<PackageTableProps> = ({
  pricingPackages,
  sections,
  title,
  contactLink,
  language = APP_LANGUAGES.ENGLISH
}) => {
  // Sort the pricingPackages array by price
  pricingPackages?.sort((a, b) => {
    const priceA = typeof a.price === "string" ? parseFloat(a.price) : parseFloat(a.price?.discounted || "");
    const priceB = typeof b.price === "string" ? parseFloat(b.price) : parseFloat(b.price?.discounted || "");

    return priceA - priceB;
  });
  sections?.forEach((section) => {
    section.features?.sort((a, b) => {
      const countA = Object.values(a.pricingPackages || []).filter((v) => v).length;
      const countB = Object.values(b.pricingPackages || []).filter((v) => v).length;

      return countB - countA;
    });
  });
  return (
    <div id="PackageTable-74" className="bg-[#FFFDF6]">
      <div className="tile-container">
        <div className="relative">
          <div className="mx-auto max-w-5xl">
            <div className="tile-title-container">
              <h2 className="tile-main-header">{title}</h2>
              <p className="tile-subtitle"></p>
              <p className="mt-4 text-center text-xl font-semibold">
                Get help choosing.
                <Link href={contactLink || "#"} className="ml-2 inline text-[#E5B04F]">
                  <span>
                    <Contact language={language} />
                  </span>
                  <ChevronRightIcon className="inline h-5 w-5" aria-hidden="true" />
                </Link>
              </p>
            </div>
          </div>
          <div className="sticky top-0 z-10 mt-8 hidden  bg-[#FFFDF6] pt-4 sm:mt-10 lg:block ">
            <div className={`grid grid-cols-${(pricingPackages?.length ?? 0) + 1} gap-x-8 before:block`}>
              {pricingPackages?.map((pricingPackage) => (
                <div
                  key={pricingPackage.id}
                  className={classNames(
                    pricingPackage.featured ? "text-primary-700" : "text-primary-700",
                    "inline-flex items-center text-xl font-semibold",
                    pricingPackage.featured ? "border-[#E5B04F]" : "border-gray-900/10",
                    "border-b-[1px] pb-4"
                  )}
                >
                  <span className="mr-2 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-neutral-300 bg-[#f5f1e4]">
                    <Image src={pricingPackage.iconUrl || ""} alt="test" width={34} height={34} />
                  </span>
                  {pricingPackage.name}
                </div>
              ))}
            </div>
          </div>
          {/* Feature comparison (lg+) */}
          <section aria-labelledby="comparison-heading" className=" hidden lg:block">
            <h2 id="comparison-heading" className="sr-only">
              Feature comparison
            </h2>
            <div className={`grid grid-cols-${(pricingPackages?.length ?? 0) + 1} gap-x-8 before:block`}>
              {pricingPackages?.map((pricingPackage) => (
                <div key={pricingPackage.id} aria-hidden="true" className="-mt-px ">
                  <div
                    className={classNames(
                      pricingPackage.featured ? "border-[#E5B04F]" : "border-gray-900/10",
                      "border-t-[1px] pt-4"
                    )}
                  >
                    <p className="mb-4 font-matter text-sm font-normal leading-6 text-primary-600">
                      {pricingPackage.description}
                    </p>
                    <div className="my-4 flex items-baseline  text-xl font-medium leading-6 text-gray-500">
                      {pricingPackage.price
                        ? typeof pricingPackage.price === "string"
                          ? formatToCurrency(pricingPackage.price)
                          : formatToCurrency(pricingPackage.price.discounted)
                        : null}
                      <span className="ml-2 inline-block justify-end align-bottom text-xl font-medium line-through">
                        {formatToCurrency(
                          pricingPackage.total ||
                            (typeof pricingPackage.price === "string"
                              ? pricingPackage.price
                              : pricingPackage.price?.total || "0")
                        )}
                      </span>
                    </div>
                    <div className="w-full">
                      <Link
                        href={pricingPackage.href || "#"}
                        className={getButtonVariantStylesAndSize({ variant: ButtonVariantTypes.DEFAULT, size: "lg" })}
                      >
                        Get Started
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12 space-y-16">
              {sections?.map((section) => (
                <div key={section.name}>
                  <h3 className="text-sm font-semibold leading-6 text-base-primary">{section.name}</h3>
                  <div className="relative -mx-8 mt-2">
                    {/* Fake card backgrounds */}
                    <div
                      className={`absolute inset-x-8 inset-y-0 grid grid-cols-${
                        (pricingPackages?.length ?? 0) + 1
                      } gap-x-8 before:block`}
                      aria-hidden="true"
                    >
                      {pricingPackages?.map((pricingPackage) => (
                        <div key={pricingPackage.id} className="h-full w-full rounded-lg bg-white shadow-sm" />
                      ))}
                    </div>

                    <table className="relative w-full border-separate border-spacing-x-8">
                      <thead>
                        <tr className="text-left">
                          <th scope="col">
                            <span className="sr-only">Feature</span>
                          </th>
                          {pricingPackages?.map((pricingPackage) => (
                            <th key={pricingPackage.id} scope="col">
                              <span className="sr-only">{pricingPackage.name} package</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.features?.map((feature, featureIdx) => (
                          <tr key={feature.name}>
                            <th
                              scope="row"
                              className="w-1/4 py-3 pr-4 text-left text-md font-normal leading-10 text-[#222222]"
                            >
                              {feature.name}
                              {featureIdx !== (section.features?.length || 0) - 1 ? (
                                <div className="absolute inset-x-8 mt-3 h-px bg-[#E5E7EB]" />
                              ) : null}
                            </th>
                            {pricingPackages?.map((pricingPackage) => (
                              <td key={pricingPackage.id} className="relative w-1/4 px-4 py-0 text-center">
                                <span className="relative h-full w-full py-3">
                                  {typeof feature.pricingPackages?.[pricingPackage.id] === "string" ? (
                                    <span
                                      className={
                                        pricingPackage.featured ? "font-medium text-base-primary" : "text-base-primary"
                                      }
                                    >
                                      {feature.pricingPackages[pricingPackage.id]}
                                    </span>
                                  ) : (
                                    <>
                                      {feature.pricingPackages?.[pricingPackage.id] === true ? (
                                        <Check className="mx-auto text-airbnbgreen" strokeWidth={3} />
                                      ) : (
                                        <Minus className="mx-auto  text-gray-400" strokeWidth={3} />
                                      )}
                                      <span className="sr-only">
                                        {feature.pricingPackages?.[pricingPackage.id] === true ? "Yes" : "No"}
                                      </span>
                                    </>
                                  )}
                                </span>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {/* Fake card borders */}
                    <div
                      className={`pointer-events-none absolute inset-x-8 inset-y-0 grid grid-cols-${
                        (pricingPackages?.length ?? 0) + 1
                      } gap-x-8 before:block`}
                      aria-hidden="true"
                    >
                      {pricingPackages?.map((pricingPackage) => (
                        <div
                          key={pricingPackage.id}
                          className={classNames(
                            pricingPackage.featured ? "ring-2 ring-[#E5B04F]" : "ring-1 ring-[#E5E7EB]",
                            "rounded-lg"
                          )}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PackageTable;
